import { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'

import { Breadcrumb, Container } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { PrefectureHeader, TopFooter } from '~/features'
import { OfficeDetailPage } from '~/features/office-detail-page'
import { searchPrefecture } from '~/models/master.server'
import { findOfficeById } from '~/models/office.server'
import { Office, Image } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { prefectureSlug, officeId } = params
  if (!prefectureSlug || !officeId || isNaN(Number(officeId)))
    throw new Response('Bad Request', { status: 400 })

  const [prefecture] = await searchPrefecture({ slug: prefectureSlug })
  if (!prefecture)
    throw new Response('Prefecture Not Found', {
      status: 404,
      statusText: 'Prefecture Not Found.',
    })

  const office = await findOfficeById(Number(officeId))
  if (!office)
    throw new Response('Office Not Found', {
      status: 404,
      statusText: 'Office Not Found.',
    })

  return { prefecture, office }
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const officeName = data?.office.name || ''
  const officeDescription = data?.office.description || ''
  const sortedImages = data?.office.images.sort(
    (a: Image, b: Image) => a.priority - b.priority,
  )
  const firstImage =
    sortedImages?.find((image: Image) => image.isEyeCatch) || sortedImages?.[0]
  const titlePrefix = officeName
    ? `${officeName} | オフィス詳細`
    : 'オフィス詳細'
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    { title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト' },
    { property: 'og:title', content: officeName },
    { property: 'og:description', content: officeDescription },
    {
      property: 'og:image',
      content: firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL,
    },
  ]
}

export default function Index() {
  const { prefecture, office: jsonifiedOffice } = useLoaderData<typeof loader>()
  const office: Office = {
    ...jsonifiedOffice,
    spaces: jsonifiedOffice.spaces.map((space) => ({
      ...space,
      createdAt: new Date(space.createdAt),
    })),
  }

  return (
    <>
      <PrefectureHeader prefecture={prefecture} />
      <Container>
        <Breadcrumb
          links={[
            { text: 'ホーム', href: '/' },
            { text: prefecture.name, href: `/prefectures/${prefecture.slug}` },
            { text: office.name },
          ]}
        />
      </Container>
      <OfficeDetailPage office={office} />
      <TopFooter />
      {/* 2025/04/08 zui 一旦対応、問い合わせボタンを編集するべき */}
      <div className="h-[60px]" />
    </>
  )
}
